import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { FileText, RefreshCw, Save } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';

const HistoryTab = () => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await API.get('/medical/history');
      setHistory(response.data);
      setEditText(response.data.summary_text);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error('Failed to load medical history');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put('/medical/history', { summary_text: editText });
      toast.success('Medical history updated');
      setEditing(false);
      fetchHistory();
    } catch (error) {
      toast.error('Failed to update history');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    setLoading(true);
    try {
      // Delete existing and regenerate
      await API.get('/medical/history');
      toast.success('Medical history regenerated');
      fetchHistory();
    } catch (error) {
      toast.error('Failed to regenerate history');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Medical History Summary</h3>
        <div className="flex space-x-2">
          <Button
            onClick={handleRegenerate}
            size="sm"
            variant="outline"
            className="text-blue-600 border-blue-300"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Regenerate
          </Button>
          {!editing ? (
            <Button
              onClick={() => setEditing(true)}
              size="sm"
              className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900"
            >
              <FileText className="h-4 w-4 mr-2" />
              Edit
            </Button>
          ) : (
            <Button
              onClick={handleSave}
              size="sm"
              disabled={saving}
              className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg bg-white">
        <div className="p-6">
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900">
              <strong>Clinical Summary</strong> - This automatically includes current medications, allergies, surgeries, and ongoing symptoms.
              Edit as needed for doctor visits.
            </p>
          </div>

          {editing ? (
            <Textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={20}
              className="font-mono text-sm"
            />
          ) : (
            <div className="p-6 bg-gray-50 rounded-lg">
              <pre className="whitespace-pre-wrap font-sans text-sm text-gray-900">
                {history?.summary_text || 'No medical history available.'}
              </pre>
            </div>
          )}

          {history?.last_updated && (
            <p className="text-xs text-gray-500 mt-4">
              Last updated: {new Date(history.last_updated).toLocaleString()}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default HistoryTab;